import { FeedbackGivenOnType, SimulationCaseById } from "__generated__/types";
import { useAuth } from "common/auth/Auth";
import { Button } from "common/buttons/Button";
import Chat from "common/components/gpt/Chat";
import { LoadingLayout } from "common/components/layouts/LoadingLayout";
import { NormalContentLayout } from "common/components/layouts/NormalContentLayout";
import { MultilineText } from "common/components/MultilineText";
import { NoDataError } from "common/components/NoDataError";
import { Paragraph } from "common/components/Typography";
import { useSimpleQuery } from "common/graphql/useSimpleQuery";
import { urls } from "common/urls";
import { QUERY } from "domain/common/simulation-mutations";
import { FeedbackForm } from "domain/feedback/FeedbackForm";
import { Diagnosis } from "domain/simulation/Diagnosis";
import { Examination } from "domain/simulation/Examination";
import React, { useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import styles from "./SimulationPage.module.css";

type RouteProps = {
  caseId: string;
};

export const SimulationCasePage: React.FC = () => {
  const location = useLocation();
  const { caseId } = useParams<RouteProps>();
  const me = useAuth();
  const isAdmin = me.me?.menu.admin;
  const [showDiagnosis, setShowDiagnosis] = useState(false);
  const [showChat, setShowChat] = useState(false);

  if (!caseId) {
    throw new Error("Unexpected error: Missing CaseId");
  }

  const { data, loadingOrError } = useSimpleQuery<SimulationCaseById>(QUERY, {
    variables: {
      id: caseId,
    },
  });

  if (loadingOrError) {
    return <LoadingLayout>{loadingOrError}</LoadingLayout>;
  }

  if (!data || !data.simulationCaseById) {
    return (
      <LoadingLayout>
        <NoDataError />
      </LoadingLayout>
    );
  }

  const { title, patientInformation } = data.simulationCaseById;

  const canGiveFeedback = me.me?.menu.feedback && caseId;

  return (
    <NormalContentLayout title={title}>
      {patientInformation ? (
        <MultilineText
          content={patientInformation.content}
          isMarkdown={patientInformation.isMarkdown}
        />
      ) : (
        <Paragraph>Ingen pasientinformasjon er tilgjengelig.</Paragraph>
      )}

      {!showDiagnosis && !showChat && <Examination data={data} />}

      {showDiagnosis && (
        <Diagnosis
          data={data}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          overrideNextCase={location.state ? location.state["cases"] : []}
          setShowDiagnosis={setShowDiagnosis}
        />
      )}

      {showChat && (
        <Chat
          caseData={data}
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
          overrideNextCase={location.state ? location.state["cases"] : []}
          setShowChat={setShowChat}
        />
      )}

      {!showDiagnosis && !showChat && (
        <div className={styles.cancelOrSubmitContainer}>
          <Link to={urls.simulation}>
            <Button>Gå tilbake</Button>
          </Link>
          <div>
            <Button
              onClick={() => {
                setShowDiagnosis(!showDiagnosis);
              }}
            >
              Sett diagnose
            </Button>
            <Button
              onClick={() => {
                setShowChat(!showChat);
              }}
            >
              Chat med pasient
            </Button>
          </div>
        </div>
      )}

      {isAdmin ? (
        <>
          <Link to={urls.simulationCaseAdmin(caseId)}>Rediger detaljer</Link>
        </>
      ) : null}

      {canGiveFeedback && (
        <FeedbackForm
          givenOnReferenceId={caseId}
          givenOnType={FeedbackGivenOnType.SimulationCase}
        />
      )}
    </NormalContentLayout>
  );
};
